/**
 * MFA OTP Verification Screen
 * UI Component - Presentation layer only
 * Delegates all business logic to MFAController
 */

import React, { useState, useEffect, useRef } from "react";
import { CircularProgress, Alert, InputBase } from "@mui/material";
import SecurityIcon from "@mui/icons-material/Security";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import EmailIcon from "@mui/icons-material/Email";
import { fontFamily } from "../Constants/constants";
import InstaColors from "../Constants/constants";
import logo from "../../images/Extractor logo.png";
import "./MFAOtp.css";

// Import controller for business logic (same folder)
import {
  MFA_CONFIG,
  handleOTPVerification,
  handleOTPResend,
  handleOTPInputChange,
  handleOTPBackspace,
  handleOTPPaste,
  isOTPComplete,
  resetOTP,
  getMFAPreferencesFromSession,
  clearMFASession,
  getDeliveryDisplayInfo,
} from "./MFAController";

interface MFAOtpProps {
  history?: any;
}

const MFAOtp: React.FC<MFAOtpProps> = ({ history }) => {
  // In the two-step MFA model, JWT is NOT in localStorage yet.
  // MFA data is stored in sessionStorage by Login.js.
  const mfaUserId = Number(sessionStorage.getItem("mfaUserId") || "0");
  const mfaFirstName = sessionStorage.getItem("mfaFirstName") || "";

  // Get MFA preferences from session (set during login)
  const { showEmail, showPhone, mobileNumber } = getMFAPreferencesFromSession();

  // UI State
  const [otp, setOtp] = useState<string[]>(resetOTP());
  const [isVerifying, setIsVerifying] = useState<boolean>(false);
  const [isSending, setIsSending] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [success, setSuccess] = useState<boolean>(false);
  const [resendSuccess, setResendSuccess] = useState<boolean>(false);

  // Timer State
  const [canResend, setCanResend] = useState<boolean>(false);
  const [resendTimer, setResendTimer] = useState<number>(MFA_CONFIG.RESEND_TIMER_SECONDS);
  const [otpSent, setOtpSent] = useState<boolean>(false);

  // Refs for input focus management
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  /**
   * Initialize component - OTP already sent by backend after login
   */
  useEffect(() => {
    setOtpSent(true);
    setCanResend(false);
    setResendTimer(MFA_CONFIG.RESEND_TIMER_SECONDS);
  }, []);

  /**
   * Resend timer countdown
   */
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;

    if (resendTimer > 0 && !canResend) {
      interval = setInterval(() => {
        setResendTimer((prev) => {
          if (prev <= 1) {
            setCanResend(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [resendTimer, canResend]);

  /**
   * Auto-focus first input on mount
   */
  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  /**
   * Auto-verify when OTP is complete
   */
  useEffect(() => {
    if (MFA_CONFIG.AUTO_VERIFY_ON_COMPLETE && isOTPComplete(otp)) {
      // Clear any existing error before auto-verification
      setError("");
      verifyOTP(otp.join(""));
    }
  }, [otp]);

  /**
   * Handle OTP input change
   */
  const handleOtpChange = (index: number, value: string) => {
    const result = handleOTPInputChange(value, index, otp);
    setOtp(result.newOtp);

    // Focus management
    if (result.shouldFocusNext) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  /**
   * Handle key down events (backspace, paste)
   */
  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      const result = handleOTPBackspace(index, otp[index], otp);
      setOtp(result.newOtp);

      if (result.shouldFocusPrev) {
        inputRefs.current[index - 1]?.focus();
      }
    }
  };

  /**
   * Handle paste event
   */
  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData("text");
    const parsedOtp = handleOTPPaste(pastedText);

    if (parsedOtp) {
      // Clear any existing error before setting pasted OTP
      setError("");
      setOtp(parsedOtp);
      // Focus last input
      inputRefs.current[MFA_CONFIG.OTP_LENGTH - 1]?.focus();

      // Auto-verify if complete (note: this will be handled by useEffect)
      // Removed duplicate verification call to avoid race condition
    }
  };

  /**
   * Verify OTP with backend
   */
  const verifyOTP = async (otpCode: string) => {
    try {
      setIsVerifying(true);
      setError("");

      const result = await handleOTPVerification(otp);

      if (result.success) {
        // Mark MFA as verified in context

        // Show success message
        setSuccess(true);

        // Clear session data
        clearMFASession();

        // Redirect to dashboard after delay
        setTimeout(() => {
          history.push({
            pathname: "/dashboard",
            state: { someValue: true },
          });
        }, MFA_CONFIG.REDIRECT_DELAY_MS);
      } else {
        setError(result.message || "Invalid OTP. Please try again.");
        setOtp(resetOTP());
        inputRefs.current[0]?.focus();
      }
    } catch (err: any) {
      console.error("❌ Exception in verifyOTP:", err);
      setError("An error occurred. Please try again.");
      setOtp(resetOTP());
      inputRefs.current[0]?.focus();
    } finally {
      setIsVerifying(false);
    }
  };

  /**
   * Handle manual verify button click
   */
  const handleVerifyClick = () => {
    const otpString = otp.join("");
    if (otpString.length === MFA_CONFIG.OTP_LENGTH) {
      verifyOTP(otpString);
    } else {
      setError("Please enter all 6 digits");
    }
  };

  /**
   * Handle resend OTP
   */
  const handleResend = async () => {
    if (!canResend || isSending) return;

    try {
      setIsSending(true);
      setError("");

      const result = await handleOTPResend(mfaUserId, showEmail, showPhone);

      if (result.success) {
        setOtpSent(true);
        setCanResend(false);
        setResendTimer(MFA_CONFIG.RESEND_TIMER_SECONDS);
        setResendSuccess(true);
        setTimeout(() => setResendSuccess(false), 3000);
      } else {
        setError(result.message || "Failed to send OTP. Please try again.");
      }
    } catch (err: any) {
      console.error("Error resending OTP:", err);
      setError("Failed to send OTP. Please try again.");
    } finally {
      setIsSending(false);
    }
  };

  /**
   * Handle cancel/go back
   */
  const handleCancel = () => {
    clearMFASession();
    history.push("/login");
  };

  /**
   * Get delivery info for display
   */
  const deliveryInfo = getDeliveryDisplayInfo(showEmail, showPhone, mobileNumber, mfaFirstName);

  /**
   * Render appropriate icon based on delivery method
   */
  const renderIcon = () => {
    const iconStyle = { fontSize: 48, color: InstaColors.primary };

    switch (deliveryInfo.iconType) {
      case "both":
        return <SecurityIcon style={iconStyle} />;
      case "phone":
        return <PhoneAndroidIcon style={iconStyle} />;
      case "email":
      default:
        return <EmailIcon style={iconStyle} />;
    }
  };

  return (
    <div
      className="mfa-otp-container"
      style={{
        backgroundImage: `url(${process.env.PUBLIC_URL}/background.png)`,
      }}
    >
      <div className="mfa-otp-card">
        {/* Close Button */}
        {/* <IconButton 
          className="mfa-otp-close" 
          onClick={handleCancel}
          aria-label="close"
        >
          <CloseIcon />
        </IconButton> */}

        {/* Logo */}
        <div className="mfa-otp-logo">
          <img src={logo} alt="logo" />
        </div>

        {/* Icon */}
        <div className="mfa-otp-icon">{renderIcon()}</div>

        {/* Title */}
        {/* <h2 className="mfa-otp-title" style={{ fontFamily }}>
          Verify Your Identity
        </h2> */}

        {/* Description */}
        <p className="mfa-otp-description" style={{ fontFamily }}>
          {deliveryInfo.text}
        </p>
        {deliveryInfo.detail && (
          <p className="mfa-otp-detail" style={{ fontFamily }}>
            {deliveryInfo.detail}
          </p>
        )}

        {/* OTP Input Boxes */}
        <div className="mfa-otp-inputs">
          {otp.map((digit, index) => (
            <InputBase
              key={index}
              inputRef={(el) => (inputRefs.current[index] = el)}
              value={digit}
              onChange={(e) => handleOtpChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={handlePaste}
              className="mfa-otp-input"
              inputProps={{
                maxLength: 1,
                style: {
                  textAlign: "center",
                  fontSize: 24,
                  fontWeight: "bold",
                  fontFamily,
                },
              }}
              disabled={isVerifying}
            />
          ))}
        </div>

        {/* Error Message */}
        {error && (
          <Alert severity="error" className="mfa-otp-alert mfa-otp-error">
            {error}
          </Alert>
        )}

        {/* Success Message */}
        {success && (
          <Alert severity="success" className="mfa-otp-alert mfa-otp-success">
            OTP verified successfully!
          </Alert>
        )}

        {/* Resend Success Message */}
        {resendSuccess && (
          <Alert severity="success" className="mfa-otp-alert mfa-otp-success">
            OTP resent successfully!
          </Alert>
        )}

        {/* Verify Button */}
        <button
          className="Button"
          onClick={handleVerifyClick}
          disabled={isVerifying || isOTPComplete(otp) === false}
          style={{ fontFamily }}
        >
          {isVerifying ? (
            <>
              <CircularProgress
                size={14}
                thickness={5}
                style={{ marginRight: 8, color: "white" }}
              />
              Verifying...
            </>
          ) : (
            "Verify OTP"
          )}
        </button>

        {/* Resend Section */}
        <div className="mfa-otp-resend" style={{ fontFamily }}>
          {canResend ? (
            <button
              className="mfa-otp-resend-button"
              onClick={handleResend}
              disabled={isSending}
              style={{ fontFamily }}
            >
              {isSending ? "Sending..." : "Resend Code"}
            </button>
          ) : (
            <span className="mfa-otp-timer">Resend code in {resendTimer}s</span>
          )}
        </div>
      </div>
    </div>
  );
};

export default MFAOtp;
